<template>
  <div v-if="isOpen" class="fixed inset-0 bg-gray-50 md:bg-black md:bg-opacity-50 z-50 flex items-start md:items-center justify-center" @click="onClose">
    <!-- Friends List popup -->
    <div class="bg-gray-50 md:bg-white md:rounded-3xl w-full h-full md:h-auto md:max-w-md md:mx-4 overflow-y-auto scrollbar-hide" @click.stop>
      <div class="p-6 pt-10 md:pt-6">
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-2xl font-bold nav-font">Friends List</h2>
          <button @click="onClose" class="text-gray-500 hover:text-gray-700 p-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Search Bar -->
        <div class="mb-6">
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              v-model="searchQuery"
              class="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base md:text-sm"
              placeholder="Search friends..."
            />
          </div>
        </div>

        <!-- Friends List -->
        <div class="space-y-4 mb-6">
          <div v-if="filteredFriends.length === 0" class="text-center py-8">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            <p class="text-gray-500">{{ searchQuery ? 'No friends match your search' : 'No friends yet' }}</p>
            <button class="mt-4 px-4 py-2 bg-blue-500 text-white rounded-full text-sm font-medium hover:bg-blue-600 transition-colors">
              Add Friends
            </button>
          </div>

          <div v-for="friend in filteredFriends" :key="friend.id" class="flex items-center justify-between p-3 bg-white rounded-xl shadow-sm border border-gray-100">
            <div class="flex items-center">
              <div class="w-12 h-12 rounded-full overflow-hidden mr-3">
                <img :src="friend.picture" alt="Friend" class="w-full h-full object-cover" />
              </div>
              <div>
                <h3 class="font-medium text-gray-900">{{ friend.name }}</h3>
                <p class="text-sm text-gray-500">{{ friend.email }}</p>
              </div>
            </div>
            <div class="flex space-x-2">
              <button class="p-2 text-blue-500 hover:text-blue-700 transition-colors" @click="viewProfile(friend)">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </button>
              <button class="p-2 text-red-500 hover:text-red-700 transition-colors" @click="removeFriend(friend)">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Add Friend Button -->
        <div class="sticky bottom-0 bg-gray-50 md:bg-white pt-4 pb-8 md:pb-4">
          <button class="w-full py-3 bg-blue-500 text-white rounded-full text-sm font-medium hover:bg-blue-600 transition-colors flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
            </svg>
            Add New Friend
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'

export default {
  name: 'FriendsListPopup',
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    user: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'removeFriend'],
  setup(props, { emit }) {
    const searchQuery = ref('')

    // Get friends from user object
    const friends = computed(() => {
      return props.user.friends || []
    })

    // Filter friends based on search query
    const filteredFriends = computed(() => {
      if (!searchQuery.value) return friends.value

      const query = searchQuery.value.toLowerCase()
      return friends.value.filter(friend =>
        friend.name.toLowerCase().includes(query) ||
        friend.email.toLowerCase().includes(query)
      )
    })

    // viewProfile
    const viewProfile = () => {
      // safe
    }
    
    // Remove friend
    const removeFriend = (friend) => {
      if (confirm(`Are you sure you want to remove ${friend.name} from your friends list?`)) {// In a real app, you would call an API to remove the friend
        // This would typically be handled through a Vuex action
        emit('removeFriend', friend)
      }
    }

    const onClose = () => {
      emit('close')
    }

    return {
      searchQuery,
      filteredFriends,
      viewProfile,
      removeFriend,
      onClose
    }
  }
}
</script>

<style scoped>
.nav-font {
  font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
}

/* Smooth transitions */
.transition-colors {
  transition: all 0.2s ease;
}

/* Custom focus styles */
input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

/* Hide scrollbar but keep scrolling functionality */
.scrollbar-hide {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;     /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;             /* Chrome, Safari and Opera */
}
</style>
